import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  IconButton,
  Typography
} from '@mui';

// Allow custom dialog width values (we either use the Material UI defaults, or a custom one).
const getWidthValue = (theme, width) => {
  // It the 'maxWidth' value exists in the Material UI breakpoint array
  // (i.e. it's xs, sm, md, lg, xl), then use that breakpoint value.
  if (width in theme.breakpoints.values) {
    return theme.breakpoints.values[width];
  }

  // If it's not a xs, sm, md, etc value, then it's a custom width:
  return width;
};

const useStyles = makeStyles()((theme, { height, maxWidth }) => ({
  paper: ({
    height,
    width: getWidthValue(theme, maxWidth)
  })
}));

const styles = theme => ({
  root: {
    margin: 0,
    padding: theme.spacing(2)
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[700]
  }
});

const DialogTitle = withStyles(props => {
  // className must be destructured out here (not left in ...other) — tss-react's withStyles
  // passes it through as its own prop whenever a component has more than one style class (root
  // here, plus closeButton below), and spreading ...other after className={classes.root} was
  // silently clobbering our intended class with that other (empty) one, so this padding override
  // was never actually reaching the DOM at all.
  const { children, classes, onClose, className, ...other } = props;  // eslint-disable-line no-unused-vars
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
}, styles);

const DialogContent = withStyles(MuiDialogContent, theme => ({
  root: {
    padding: theme.spacing(2),
    // v5 added a same-specificity `.MuiDialogTitle-root + &` rule that zeroes paddingTop
    // whenever a title precedes the content without dividers (v4 had no such rule) — the
    // doubled class selector below matches that rule's specificity so ours wins instead,
    // restoring the uniform padding every dialog here was built around.
    '&&': {
      paddingTop: theme.spacing(2)
    }
  }
}));

const DialogActions = withStyles(MuiDialogActions, theme => ({
  root: {
    margin: 0,
    padding: theme.spacing(1)
  }
}));

const CustomizedDialog = ({
  actions,
  dividers = false,
  children,
  contentStyle,
  fullWidth = true,
  height = 'auto',
  maxWidth = 'sm',
  onClose,
  open = true,
  title
}) => {
  const { classes } = useStyles({ height, maxWidth });
  return (
    <Dialog
      aria-labelledby="customized-dialog-title"
      classes={{ paper: classes.paper }}
      fullWidth={fullWidth}
      maxWidth={false}
      onClose={onClose}
      open={open}
    >
      <DialogTitle id="customized-dialog-title" onClose={onClose}>
        {title}
      </DialogTitle>
      <DialogContent dividers={dividers} style={contentStyle}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions>
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

CustomizedDialog.propTypes = {
  actions: PropTypes.node,
  children: PropTypes.node,
  contentStyle: PropTypes.object,
  dividers: PropTypes.bool,
  fullWidth: PropTypes.bool,
  height: PropTypes.string,
  maxWidth: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.string
};

export default CustomizedDialog;
